import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription';
import { toast } from 'react-toastify';
import InternalNavbar from '../../components/InternalNavbar';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
    FaCrown,
    FaArrowUp,
    FaRedo,
    FaCalendar,
    FaClock,
    FaChartLine,
    FaCheck,
    FaTimes,
    FaLock,
    FaGift,
    FaStar,
    FaQuestionCircle,
    FaCreditCard,
    FaDollarSign,
    FaMobileAlt,
    FaHeadset,
    FaExclamationTriangle
} from 'react-icons/fa';

import './style.css';

const SubscriptionPage = () => {
    const navigate = useNavigate();
    const {
        subscription,
        loading,
        hasActiveSubscription,
        isDemoSubscription,
        isPaidSubscription,
        isExpired,
        daysRemaining
    } = useSubscription();

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleSubscribe = () => {
        toast.info('Redirecting to payment...');
        navigate('/subscribe');
    };

    const handleUpgrade = () => {
        toast.info('Redirecting to upgrade...');
        navigate('/subscribe');
    };

    if (loading) {
        return (
            <div className="subscription-page">
                <InternalNavbar />
                <div className="subscription-container">
                    <div className="subscription-wrapper">
                        <LoadingSpinner
                            size="large"
                            text="Loading subscription details..."
                            fullScreen={false}
                        />
                    </div>
                </div>
                <Footer />
                <Scrollbar />
            </div>
        );
    }

    return (
        <div className="subscription-page">
            <InternalNavbar />

            <div className="subscription-container">
                <div className="subscription-wrapper">
                    {/* Hero Section */}
                    <div className="subscription-hero">
                        <div className="hero-content">
                            <div className="hero-text">
                                <h1>My Subscription</h1>
                                <p>Manage your subscription and unlock your learning potential</p>
                            </div>
                            <div className="hero-icon">
                                <FaCrown />
                            </div>
                        </div>
                    </div>

                    {/* Current Subscription Status */}
                    {hasActiveSubscription ? (
                        <div className="current-subscription-section">
                            <div className="section-header">
                                <h2>Current Subscription</h2>
                                <p>Your active plan details and benefits</p>
                            </div>

                            <div className="subscription-status-card">
                                <div className="status-header">
                                    <div className="status-badge-container">
                                        <span className={`status-badge ${isDemoSubscription ? 'demo' : isPaidSubscription ? 'premium' : 'expired'}`}>
                                            {isDemoSubscription ? <FaGift /> : isPaidSubscription ? <FaCrown /> : <FaExclamationTriangle />}
                                            {isDemoSubscription ? 'Demo Access' : isPaidSubscription ? 'Premium Active' : 'Expired'}
                                        </span>
                                    </div>
                                    <div className="status-actions">
                                        {isDemoSubscription && (
                                            <button className="upgrade-btn" onClick={handleUpgrade}>
                                                <FaArrowUp />
                                                Upgrade Now
                                            </button>
                                        )}
                                        {isPaidSubscription && daysRemaining <= 60 && (
                                            <button className="renew-btn" onClick={handleSubscribe}>
                                                <FaRedo />
                                                Renew Plan
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="subscription-details-grid">
                                    <div className="detail-card">
                                        <div className="detail-icon">
                                            <FaCalendar />
                                        </div>
                                        <div className="detail-info">
                                            <span className="detail-label">Start Date</span>
                                            <span className="detail-value">{formatDate(subscription?.start_date)}</span>
                                        </div>
                                    </div>

                                    <div className="detail-card">
                                        <div className="detail-icon">
                                            <FaClock />
                                        </div>
                                        <div className="detail-info">
                                            <span className="detail-label">End Date</span>
                                            <span className="detail-value">{formatDate(subscription?.end_date)}</span>
                                        </div>
                                    </div>

                                    <div className="detail-card">
                                        <div className="detail-icon">
                                            <FaChartLine />
                                        </div>
                                        <div className="detail-info">
                                            <span className="detail-label">Plan</span>
                                            <span className="detail-value">
                                                {subscription?.plan_interval === 'month' ? 'Monthly' : 'Yearly'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="detail-card">
                                        <div className="detail-icon">
                                            <FaChartLine />
                                        </div>
                                        <div className="detail-info">
                                            <span className="detail-label">Status</span>
                                            <span className={`detail-value status-${subscription?.status}`}>
                                                {subscription?.status?.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>

                                    {daysRemaining !== null && (
                                        <div className="detail-card">
                                            <div className="detail-icon">
                                                <FaClock />
                                            </div>
                                            <div className="detail-info">
                                                <span className="detail-label">Days Remaining</span>
                                                <span className="detail-value highlight">{daysRemaining} days</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {isDemoSubscription && (
                                    <div className="demo-benefits">
                                        <h3>Demo Benefits & Limitations</h3>
                                        <div className="benefits-grid">
                                            <div className="benefit-item included">
                                                <FaCheck />
                                                <span>14 days access</span>
                                            </div>
                                            <div className="benefit-item included">
                                                <FaCheck />
                                                <span>First 2 lessons per level</span>
                                            </div>
                                            <div className="benefit-item included">
                                                <FaCheck />
                                                <span>Basic assessments</span>
                                            </div>
                                            <div className="benefit-item excluded">
                                                <FaTimes />
                                                <span>Full course library</span>
                                            </div>
                                            <div className="benefit-item excluded">
                                                <FaTimes />
                                                <span>Advanced features</span>
                                            </div>
                                            <div className="benefit-item excluded">
                                                <FaTimes />
                                                <span>Certificates</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="no-subscription-section">
                            <div className="no-subscription-card">
                                <div className="no-sub-icon">
                                    <FaLock />
                                </div>
                                <h2>No Active Subscription</h2>
                                <p>Choose a plan below to start your Arabic learning journey and unlock all features</p>
                                <div className="quick-actions">
                                    <button className="quick-action-btn primary" onClick={() => navigate('/subscribe')}>
                                        <FaCrown />
                                        Enroll now
                                    </button>
                                    <button className="quick-action-btn secondary" onClick={() => navigate('/demo-confirmation')}>
                                        <FaGift />
                                        Try Demo
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Upgrade to Premium Section - Show for Demo Users */}
                    {isDemoSubscription && (
                        <div className="upgrade-section">
                            <div className="section-header">
                                <h2>Upgrade to Premium</h2>
                                <p>Unlock the full potential of your Arabic learning journey</p>
                            </div>

                            <div className="upgrade-card">
                                <div className="upgrade-content">
                                    <div className="upgrade-left">
                                        <div className="upgrade-badge">
                                            <FaCrown />
                                            <span>Premium Access</span>
                                        </div>
                                        <h3>Get 2 FREE Months When You Upgrade!</h3>
                                        <p>Upgrade from your demo to premium and receive 14 months of access for the price of 12.</p>

                                        <div className="upgrade-features">
                                            <div className="upgrade-feature">
                                                <FaCheck />
                                                <span>Full access to all lessons and levels</span>
                                            </div>
                                            <div className="upgrade-feature">
                                                <FaCheck />
                                                <span>Completion certificates</span>
                                            </div>
                                            <div className="upgrade-feature">
                                                <FaCheck />
                                                <span>Advanced assessments and tracking</span>
                                            </div>
                                            <div className="upgrade-feature">
                                                <FaCheck />
                                                <span>Priority customer support</span>
                                            </div>
                                        </div>

                                        <div className="upgrade-pricing">
                                            <div className="upgrade-price">
                                                <span className="price-label">Only</span>
                                                <span className="price-amount">$49</span>
                                                <span className="price-period">/year</span>
                                            </div>
                                            <div className="upgrade-savings">
                                                <FaStar />
                                                <span>Save $10 with this special offer</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="upgrade-right">
                                        <div className="upgrade-cta">
                                            <button className="upgrade-premium-btn" onClick={() => navigate('/subscribe')}>
                                                <FaCrown />
                                                Upgrade to Premium
                                            </button>
                                            <p className="upgrade-note">
                                                <FaLock />
                                                Secure payment via Stripe
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* FAQ Section */}
                    <div className="faq-section">
                        <div className="section-header">
                            <h2>Frequently Asked Questions</h2>
                            <p>Everything you need to know about our subscription plans</p>
                        </div>

                        <div className="faq-grid">
                            <div className="faq-card">
                                <div className="faq-icon">
                                    <FaQuestionCircle />
                                </div>
                                <h4>Can I cancel anytime?</h4>
                                <p>Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period with no additional charges.</p>
                            </div>

                            <div className="faq-card">
                                <div className="faq-icon">
                                    <FaCreditCard />
                                </div>
                                <h4>What payment methods do you accept?</h4>
                                <p>We accept all major credit cards, debit cards, and digital wallets through our secure payment processor Stripe for your safety.</p>
                            </div>

                            <div className="faq-card">
                                <div className="faq-icon">
                                    <FaDollarSign />
                                </div>
                                <h4>Is there a refund policy?</h4>
                                <p>Yes, we offer a 30-day money-back guarantee if you're not satisfied with the platform. No questions asked.</p>
                            </div>

                            <div className="faq-card">
                                <div className="faq-icon">
                                    <FaArrowUp />
                                </div>
                                <h4>Can I upgrade from Demo to Premium?</h4>
                                <p>Absolutely! You can upgrade at any time and get immediate access to all premium features. Your progress will be preserved.</p>
                            </div>

                            <div className="faq-card">
                                <div className="faq-icon">
                                    <FaHeadset />
                                </div>
                                <h4>Do you offer customer support?</h4>
                                <p>Yes, we provide email support for all users, with priority support for Premium subscribers. We typically respond within 24 hours.</p>
                            </div>

                            <div className="faq-card">
                                <div className="faq-icon">
                                    <FaMobileAlt />
                                </div>
                                <h4>Can I access on multiple devices?</h4>
                                <p>Yes, your subscription works across all devices. Access your courses on desktop, tablet, or mobile with automatic progress sync.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
            <Scrollbar />
        </div>
    );
};

export default SubscriptionPage;


